/**
 * Data Replay Page
 * Återläsning av historiska GPS-data
 */

import React from 'react';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  CalendarIcon,
  TruckIcon,
  PlayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon,
  StopCircleIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { useDataReplay } from '@/hooks/useDataReplay';
import { formatBytes, JobStatus } from '@/types/dataReplay';
import HierarchicalOrgSelect from '@/components/common/HierarchicalOrgSelect';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';

import DateInput from '@/components/common/DateInput';

const getStatusIcon = (status: JobStatus) => {
  switch (status) {
    case 'completed':
      return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
    case 'running':
      return <ArrowPathIcon className="w-5 h-5 text-blue-500 animate-spin" />;
    case 'failed':
      return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
    case 'cancelled':
      return <StopCircleIcon className="w-5 h-5 text-amber-600" />;
    default:
      return <ClockIcon className="w-5 h-5 text-textTertiary" />;
  }
};

const DataReplay: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    availableDates,
    jobs,
    dataStatus,
    filteredVehicles,
    loading,
    dataStatusLoading,
    fullResetInProgress,
    organizationsLoading,
    error,
    successMessage,
    selectedOrganizationId,
    selectedImei,
    fromDate,
    toDate,
    vehicleSearch,
    isVehicleDropdownOpen,
    vehicleDropdownRef,
    setFromDate,
    setToDate,
    setSelectedOrganizationId,
    setIsVehicleDropdownOpen: _setIsVehicleDropdownOpen,
    startReplay,
    stopReplayJob,
    runFullReset,
    startReplayAll,
    loadDataStatus,
    handleVehicleSelect,
    handleVehicleSearchChange,
    handleVehicleSearchFocus,
    handleVehicleSearchBlur,
    selectAllVehiclesInOrg,
    selectDate,
  } = useDataReplay();

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(ROUTES.SYSADMIN)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
            <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{t('sysadmin.dataReplay.title')}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={loadDataStatus}
            disabled={dataStatusLoading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
            title="Kontrollera antal poster i alla databaser som full reset rensar"
          >
            {dataStatusLoading ? (
              <ArrowPathIcon className="w-5 h-5 animate-spin" />
            ) : (
              <MagnifyingGlassIcon className="w-5 h-5" />
            )}
            {t('dataReplay.verifyDataStatus')}
          </button>
          <button
            onClick={runFullReset}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            title={t('dataReplay.clearAllDescription')}
          >
            <ArrowPathIcon className="w-5 h-5" />
            {t('dataReplay.fullReset')}
          </button>
          <button
            onClick={startReplayAll}
            disabled={loading || availableDates.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
          >
            <ArrowPathIcon className="w-5 h-5" />
            {t('dataReplay.replayAllFiles')}
          </button>
        </div>
      </div>

      {fullResetInProgress && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg flex items-center gap-3">
          <ArrowPathIcon className="w-6 h-6 animate-spin flex-shrink-0" />
          <span>{t('dataReplay.fullResetInProgress')}</span>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {dataStatus && (
        <div className={`rounded-lg border p-4 ${dataStatus.allCleared ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="font-semibold mb-2 flex items-center gap-2">
            {dataStatus.allCleared ? (
              <>
                <CheckCircleIcon className="w-5 h-5 text-green-600" />
                {t('dataReplay.allDbEmpty')}
              </>
            ) : (
              <>
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-600" />
                {t('dataReplay.someTablesHaveData')}
              </>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="font-medium text-gray-700 mb-1">MongoDB</div>
              <ul className="space-y-0.5">
                {Object.entries(dataStatus.mongo).map(([k, v]) => (
                  <li key={k} className={v > 0 ? 'text-amber-700' : 'text-textSecondary'}>
                    {k}: <strong>{v}</strong>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="font-medium text-gray-700 mb-1">MariaDB</div>
              <ul className="space-y-0.5">
                {Object.entries(dataStatus.maria).map(([k, v]) => (
                  <li key={k} className={v > 0 ? 'text-amber-700' : 'text-textSecondary'}>
                    {k}: <strong>{v}</strong>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="font-medium text-gray-700 mb-1">Redis</div>
              <ul className="space-y-0.5">
                <li className={dataStatus.redis.dedupKeys > 0 ? 'text-amber-700' : 'text-textSecondary'}>dedupKeys: <strong>{dataStatus.redis.dedupKeys}</strong></li>
                <li className={dataStatus.redis.tripStateKeys > 0 ? 'text-amber-700' : 'text-textSecondary'}>tripStateKeys: <strong>{dataStatus.redis.tripStateKeys}</strong></li>
                <li className={dataStatus.redis.activeTripsSet > 0 ? 'text-amber-700' : 'text-textSecondary'}>activeTripsSet: <strong>{dataStatus.redis.activeTripsSet}</strong></li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Replay Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">{t('dataReplay.startReplaySection')}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <CalendarIcon className="w-4 h-4 inline mr-1" />
              {t('dataReplay.fromDate')}
            </label>
            <DateInput value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <CalendarIcon className="w-4 h-4 inline mr-1" />
              {t('dataReplay.toDate')}
            </label>
            <DateInput value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="md:col-span-2 lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <BuildingOffice2Icon className="w-4 h-4 inline mr-1" />
              {t('dataReplay.organization')}
            </label>
            <HierarchicalOrgSelect
              value={selectedOrganizationId}
              onChange={setSelectedOrganizationId}
              placeholder={t('dataReplay.selectOrgPlaceholder')}
              emptyLabel={t('dataReplay.selectOrgPlaceholder')}
              disabled={loading || organizationsLoading}
              className="w-full"
            />
          </div>

          <div className="relative lg:col-span-1" ref={vehicleDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <TruckIcon className="w-4 h-4 inline mr-1" />
              {t('dataReplay.vehicleImei')}
            </label>
            <div className="relative">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textTertiary" />
                <input
                  type="text"
                  value={vehicleSearch}
                  onChange={(e) => handleVehicleSearchChange(e.target.value)}
                  onFocus={handleVehicleSearchFocus}
                  onBlur={handleVehicleSearchBlur}
                  placeholder={t('dataReplay.searchPlaceholder')}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {isVehicleDropdownOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                  {!selectedOrganizationId ? (
                    <div className="px-3 py-2 text-sm text-textTertiary">{t('dataReplay.selectOrgFirst')}</div>
                  ) : (
                    <>
                      <div
                        onClick={selectAllVehiclesInOrg}
                        className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${
                          selectedImei === '' ? 'bg-blue-100' : ''
                        }`}
                      >
                        <div className="font-medium text-gray-900">{t('dataReplay.allVehiclesInOrg')}</div>
                        <div className="text-xs text-textTertiary">{t('dataReplay.allVehiclesInOrgHint')}</div>
                      </div>
                      {filteredVehicles.length === 0 ? (
                        <div className="px-3 py-2 text-textTertiary text-sm border-t border-gray-100">
                          {t('dataReplay.noVehiclesFound')}
                        </div>
                      ) : (
                        <>
                          <div className="border-t border-gray-200 my-1" />
                          {filteredVehicles.map((vehicle) => (
                            <div
                              key={vehicle.imei}
                              onClick={() => handleVehicleSelect(vehicle.imei)}
                              className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${
                                selectedImei === vehicle.imei ? 'bg-blue-100' : ''
                              }`}
                            >
                              <div className="font-medium text-gray-900">{vehicle.imei}</div>
                              {vehicle.regNumber && (
                                <div className="text-sm text-textTertiary">{vehicle.regNumber}</div>
                              )}
                            </div>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-end">
            <button
              onClick={startReplay}
              disabled={loading || !fromDate || !toDate || !selectedOrganizationId}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <PlayIcon className="w-5 h-5" />
              {t('dataReplay.startReplay')}
            </button>
          </div>
        </div>

        <p className="mt-3 text-sm text-textTertiary">
          {t('dataReplay.replayNote')}
        </p>
      </div>

      {/* Tillgängliga datum */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">{t('dataReplay.availableDates')}</h2>

        {availableDates.length === 0 ? (
          <p className="text-textTertiary">{t('dataReplay.noBinFiles')}</p>
        ) : (
          <>
            <p className="text-sm text-textTertiary mb-2">
              {t('dataReplay.clickDateHint')}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {availableDates.slice(0, 90).map(d => (
                <div
                  key={d.date}
                  onClick={() => selectDate(d.date)}
                  className="p-3 border rounded-lg cursor-pointer hover:bg-blue-50 hover:border-blue-300"
                >
                  <div className="font-medium">{d.date}</div>
                  <div className="text-sm text-textTertiary">
                    {d.fileCount} {t('dataReplay.files')} • {formatBytes(d.totalSize)}
                  </div>
                </div>
              ))}
            </div>
            {availableDates.length > 90 && (
              <p className="text-sm text-textTertiary mt-2">{t('dataReplay.showingDates', { total: availableDates.length })}</p>
            )}
          </>
        )}
      </div>

      {/* Aktiva jobb */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">{t('dataReplay.replayJobs')}</h2>

        {jobs.length === 0 ? (
          <p className="text-textTertiary">{t('dataReplay.noActiveJobs')}</p>
        ) : (
          <div className="space-y-3">
            {jobs.map(job => (
              <div key={job.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    {getStatusIcon(job.status)}
                    <div>
                      <div className="font-medium">
                        {job.request.organizationId ? (
                          <>
                            {t('dataReplay.orgLabel', { id: job.request.organizationId })}
                            {job.request.imei ? ` • IMEI ${job.request.imei}` : ` • ${t('dataReplay.allDevicesInOrg')}`}
                            {' '}• {job.request.fromDate} till {job.request.toDate}
                          </>
                        ) : (
                          <>
                            {job.request.imei ?? 'Alla fordon (global)'} • {job.request.fromDate} till{' '}
                            {job.request.toDate}
                          </>
                        )}
                      </div>
                      <div className="text-sm text-textTertiary">
                        {job.progress.processedFiles}/{job.progress.totalFiles} filer • 
                        {job.progress.deletedTrips} trips raderade • 
                        {job.progress.tripsCreated != null && job.progress.tripsCreated > 0
                          ? `${job.progress.tripsCreated} trips skapade`
                          : `${job.progress.queuedMessages} köade`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {job.status === 'running' && (
                      <button
                        type="button"
                        onClick={() => stopReplayJob(job.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                      >
                        <StopCircleIcon className="w-4 h-4" />
                        Stoppa replay
                      </button>
                    )}
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      job.status === 'completed' ? 'bg-green-100 text-green-700' :
                      job.status === 'running' ? 'bg-blue-100 text-blue-700' :
                      job.status === 'failed' ? 'bg-red-100 text-red-700' :
                      job.status === 'cancelled' ? 'bg-amber-100 text-amber-800' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {job.status}
                    </span>
                  </div>
                </div>
                {job.error && (
                  <div className="mt-2 text-sm text-red-600">{job.error}</div>
                )}
                
                {/* Steg-logg */}
                {job.stepLog && job.stepLog.length > 0 && (
                  <div className="mt-4 border-t pt-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Steg-logg:</h4>
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                      {job.stepLog.map((step, idx) => (
                        <div 
                          key={idx} 
                          className={`text-xs p-2 rounded flex items-start gap-2 ${
                            step.status === 'completed' ? 'bg-green-50 text-green-800' :
                            step.status === 'failed' ? 'bg-red-50 text-red-800' :
                            'bg-blue-50 text-blue-800'
                          }`}
                        >
                          <span className="font-mono text-textTertiary flex-shrink-0">
                            {new Date(step.timestamp).toLocaleTimeString('sv-SE')}
                          </span>
                          <span className="flex-shrink-0">
                            {step.status === 'completed' ? '✅' : 
                             step.status === 'failed' ? '❌' : '⏳'}
                          </span>
                          <div className="flex-1">
                            <span className="font-medium">{step.step}</span>
                            {step.details && (
                              <span className="ml-2 text-textSecondary">→ {step.details}</span>
                            )}
                            {step.duration && (
                              <span className="ml-2 text-textTertiary">({(step.duration / 1000).toFixed(1)}s)</span>
                            )}
                            {step.error && (
                              <div className="text-red-600 mt-1">{step.error}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DataReplay;
